import * as React from 'react';
import { getProductCarouselArray, getProductCarouselTitle } from '../../util/dataLoader';
import { GeSlider } from '../../Common/UI/client/components/GeSlider';
import classNames from 'classnames';
import './styles.scss';
import { ProductCardData } from './models';

// Responsive breakpoints
const ResponsiveBreakpoints = {
  MobilePortrait: 767,
  TabletPortrait: 1139 
};

function useScreenType() {
  const [screenType, setScreenType] = React.useState<{ isMobile: boolean }>({ isMobile: false });
  React.useEffect(() => {
    const checkScreenType = () => {
      const isMobile = window.innerWidth <= ResponsiveBreakpoints.MobilePortrait; // Up to 767px is mobile
      setScreenType({ isMobile });
    };
    checkScreenType();
    window.addEventListener("resize", checkScreenType);
    return () => window.removeEventListener("resize", checkScreenType);
  }, []);
  return screenType;
}

const ProductFamilyTileImg = ({ item }: { item: ProductCardData }) => {
  // Always use local image path for product images
  let imageUrl = '/images/generic-default-image.jpg';
  if (typeof item.fileReference === 'string' && item.fileReference.startsWith('/images/')) {
    imageUrl = item.fileReference;
  } else if (
    item.fileReference &&
    typeof item.fileReference === 'object' &&
    item.fileReference !== null &&
    item.fileReference !== undefined
  ) {
    const assetName = (item.fileReference as any).assetName;
    imageUrl = assetName.startsWith('/images/') ? assetName : '/images/gehealthcarestage/' + assetName;
  }
 
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  const getAltText = () => {
    if (item.cardAltText) {
      return item.cardAltText;
    }

    return 'Product family image';
  };
  
  const altText = getAltText();
  
  return (
    <img
      src={imageUrl}
      alt={altText}
      role="img"
      aria-label={altText}
      loading="eager"
      onLoad={() => setImageLoaded(true)}
      onError={() => setImageLoaded(true)}
      className="product-family-card-image"
      style={{ 
        width: '100%', 
        objectFit: 'cover',
        opacity: imageLoaded ? 1 : 0.8,
        transition: 'opacity 0.2s ease-out',
        willChange: 'opacity',
        transform: 'translate3d(0, 0, 0)',
        backfaceVisibility: 'hidden'
      }}
    />
  );
};

export const ProductFamily: React.FC = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const productFamilySlider = React.useRef<any>(null);
  const { isMobile } = useScreenType();

  React.useEffect(() => {
    const applyTabletSpacing = () => {
      const width = window.innerWidth;
      const sliderContainer = document.querySelector('.ge-product-family-carousel-slider-container') as HTMLElement;
      
      if (!sliderContainer) return;
      
      if (width >= 768 && width <= 1139) {
        sliderContainer.style.paddingLeft = '24px';
        sliderContainer.style.boxSizing = 'border-box';
      } else {
        sliderContainer.style.paddingLeft = '';
      }
    };

    applyTabletSpacing();
    window.addEventListener('resize', applyTabletSpacing);
    
    const timer = setTimeout(applyTabletSpacing, 100);
    
    return () => {
      window.removeEventListener('resize', applyTabletSpacing);
      clearTimeout(timer);
    };
  }, [currentSlideIndex]);

  const carouselItems = getProductCarouselArray();
  const title = getProductCarouselTitle();
  
  // If no data available, don't render the component
  if (!carouselItems || carouselItems.length === 0) {
    return null;
  }

  const getMaxSlideIndex = () => {
    const totalSlides = carouselItems.length;
    const width = window.innerWidth;
    
    let actualSlidesToShow, maxIndex;
    
    if (width <= 530) {
      actualSlidesToShow = 1;
      maxIndex = totalSlides <= actualSlidesToShow ? 0 : Math.max(0, totalSlides - actualSlidesToShow);
    } else if (width <= 767) {
      actualSlidesToShow = 2;  
      maxIndex = totalSlides <= actualSlidesToShow ? 0 : Math.max(0, totalSlides - actualSlidesToShow);
    } else if (width <= 1139) {
      if (width >= 1024) {
        actualSlidesToShow = 3;
      } else {
        actualSlidesToShow = 1;
      }
      maxIndex = totalSlides <= actualSlidesToShow ? 0 : Math.max(0, totalSlides - actualSlidesToShow);
    } else {
      actualSlidesToShow = 3;
      maxIndex = totalSlides <= actualSlidesToShow ? 0 : Math.max(0, totalSlides - actualSlidesToShow);
    }
    
    return maxIndex;
  };

  // Slick carousel settings
  const slickSettings = {
    dots: false,
    arrows: false, // Disable default Slick arrows
    infinite: false,
    autoplay: false,
    responsive: [
      {
        breakpoint: 1139,
        settings: {
          slidesToShow: 3, 
          slidesToScroll: 1, 
          swipeToSlide: false, 
          touchMove: true,
          arrows: false,
          infinite: false,
          draggable: true,
          centerMode: false, 
          variableWidth: false,
        }
      },
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          swipeToSlide: false,
          touchMove: true,
          arrows: false,
          infinite: false,
          draggable: true,
          centerMode: false,
          variableWidth: true,
        }
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          swipeToSlide: true,
          touchMove: true,
          arrows: false,
          infinite: false,
          draggable: true,
        }
      },
      {
        breakpoint: 530, //max-width
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          swipeToSlide: true,
          touchMove: true,
          arrows: false,
          infinite: false,
          draggable: true,
          variableWidth: true,
        }
      },
    ],
    slidesToScroll: 1,
    slidesToShow: 3,
    variableWidth: false, 
    ref: productFamilySlider,
    beforeChange: (currentIndex: number, nextIndex: number) => {
      setCurrentSlideIndex(nextIndex);
    },
    afterChange: (currentSlide: number) => {
      
      const width = window.innerWidth;
      if (width >= 768 && width <= 1139) {
        setTimeout(() => {
          const sliderContainer = document.querySelector('.ge-product-family-carousel-slider-container') as HTMLElement;
          if (sliderContainer) {
            sliderContainer.style.paddingLeft = '24px';
            sliderContainer.style.boxSizing = 'border-box';
          }
        }, 10);
      }
    },
    speed: 300,
    cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    waitForAnimate: true,
    useTransform: true,
    useCSS: true,
    swipe: true,
    touchMove: true,
    fade: false,
  };



  const triggerChangeSlide = (changeIndex: number) => {
    
    if (changeIndex >= 0 && productFamilySlider.current) {
      
      setTimeout(() => {
        if (productFamilySlider.current && productFamilySlider.current.slickGoTo) {
          productFamilySlider.current.slickGoTo(changeIndex);
        }
      }, 10);
    } else {
      console.log('Cannot trigger slide change - ref not available or invalid index');
    }
  };

  const tabSlider = carouselItems?.length > 0
    ? carouselItems.map((cardItem: any, index: number) => {
        const lastCardId =
          index + 1 === carouselItems.length
            ? `product-family-last-card-default`
            : "";
        
        const hasLink = Boolean(cardItem.cardImageLink);
        
        const imageContent = (
          <>
            <div 
              className={`ge-product-family-carousel-card-image ${hasLink ? 'has-link' : 'no-link'}`}
              {...(hasLink && {
                role: "link",
                tabIndex: 0,
                "aria-label": cardItem.cardName ? cardItem.cardName.replace(/<[^>]*>/g, '') : 'Product link'
              })}
              onClick={hasLink ? () => {
                if (cardItem.cardLinkTarget === '_blank') {
                  window.open(cardItem.cardImageLink, '_blank');
                } else {
                  window.location.href = cardItem.cardImageLink;
                }
              } : undefined}
              onKeyDown={hasLink ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (cardItem.cardLinkTarget === '_blank') {
                    window.open(cardItem.cardImageLink, '_blank');
                  } else {
                    window.location.href = cardItem.cardImageLink;
                  }
                }
              } : undefined}
            >
              <ProductFamilyTileImg item={cardItem} />
            </div>
            <div 
              className="ge-product-family-carousel-card-name text-paragraph"
              dangerouslySetInnerHTML={{ __html: cardItem.cardName }}
            />
          </>
        );
        
        return (
          <div 
            key={typeof cardItem.fileReference === 'string' 
              ? `carousel-item-${index}` 
              : cardItem.fileReference?.dmId || `carousel-item-${index}`}
            className="ge-product-card-carousel-item"
            id={lastCardId}
          >
            {hasLink ? (
              <a
                href={cardItem.cardImageLink}
                target={cardItem.cardLinkTarget}
                className="ge-product-family-carousel-card has-link"
                data-link-url={cardItem.cardImageLink}
                data-link-target={cardItem.cardLinkTarget}
                tabIndex={-1}
                aria-hidden="true"
              >
                {imageContent}
              </a>
            ) : (
              <div className="ge-product-family-carousel-card no-link">
                {imageContent}
              </div>
            )}
          </div>
        );
      })
    : null;

  return (
    <div className={`ge-product-family-hero`}>
      <div className="ge-product-family-header-container">
        <div className="ge-product-family-header">
          {title && <h2
            className="product-family-slider__title title-m"
            id={`product-family-title-default`}
            dangerouslySetInnerHTML={{ __html: title }}
          />}
          {!isMobile && getMaxSlideIndex() > 0 && (
            <div className="ge-product-family-carousel-paddles" role="group" aria-label="Carousel navigation">
              <button
                type="button"
                className={classNames(
                  "ge-product-family-carousel-paddle-left",
                  {
                    "paddle-inactive": currentSlideIndex === 0,
                    "paddle-active": currentSlideIndex > 0,
                  }
                )}
                onClick={() =>
                  currentSlideIndex > 0 &&
                  triggerChangeSlide(currentSlideIndex - 1)
                }
                disabled={currentSlideIndex === 0}
                aria-label="Previous slide"
                aria-controls="ge-product-family-carousel-slider"
                tabIndex={0}
              >
                <div className="ge-product-family-carousel-paddle-arrow" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M11.2496 15.0005C11.184 15.0013 11.119 14.9884 11.0587 14.9626C10.9984 14.9367 10.9442 14.8986 10.8996 14.8505L4.39957 8.35054C4.35173 8.30565 4.31361 8.25143 4.28755 8.19124C4.26149 8.13104 4.24805 8.06614 4.24805 8.00054C4.24805 7.93495 4.26149 7.87005 4.28755 7.80985C4.31361 7.74965 4.35173 7.69543 4.39957 7.65054L10.8996 1.15054C10.9445 1.10271 10.9987 1.06459 11.0589 1.03853C11.1191 1.01247 11.184 0.999023 11.2496 0.999023C11.3152 0.999023 11.3801 1.01247 11.4403 1.03853C11.5005 1.06459 11.5547 1.10271 11.5996 1.15054C11.6474 1.19543 11.6855 1.24965 11.7116 1.30985C11.7376 1.37005 11.7511 1.43495 11.7511 1.50054C11.7511 1.56614 11.7376 1.63104 11.7116 1.69124C11.6855 1.75143 11.6474 1.80565 11.5996 1.85054L5.45957 8.00054L11.5996 14.1505C11.6474 14.1954 11.6855 14.2496 11.7116 14.3098C11.7376 14.37 11.7511 14.4349 11.7511 14.5005C11.7511 14.5661 11.7376 14.631 11.7116 14.6912C11.6855 14.7514 11.6474 14.8057 11.5996 14.8505C11.5549 14.8986 11.5007 14.9367 11.4404 14.9626C11.3802 14.9884 11.3151 15.0013 11.2496 15.0005Z" fill="grey"></path>
                  </svg>
                </div>
              </button>
              <button
                type="button"
                className={classNames(
                  "ge-product-family-carousel-paddle-right",
                  {
                    "paddle-inactive": currentSlideIndex >= getMaxSlideIndex(),
                    "paddle-active": currentSlideIndex < getMaxSlideIndex(),
                  }
                )}
                onClick={() =>
                  currentSlideIndex < getMaxSlideIndex() && triggerChangeSlide(currentSlideIndex + 1)
                }
                disabled={currentSlideIndex >= getMaxSlideIndex()}
                aria-label="Next slide"
                aria-controls="ge-product-family-carousel-slider"
                tabIndex={0}
              >
                <div className="ge-product-family-carousel-paddle-arrow" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.74957 15.0005C4.68399 15.0013 4.61897 14.9884 4.55869 14.9626C4.49842 14.9367 4.44421 14.8986 4.39957 14.8505C4.35173 14.8057 4.31361 14.7514 4.28755 14.6912C4.26149 14.631 4.24805 14.5661 4.24805 14.5005C4.24805 14.4349 4.26149 14.37 4.28755 14.3098C4.31361 14.2496 4.35173 14.1954 4.39957 14.1505L10.5396 8.00054L4.39957 1.85054C4.35173 1.80565 4.31361 1.75143 4.28755 1.69124C4.26149 1.63104 4.24805 1.56614 4.24805 1.50054C4.24805 1.43495 4.26149 1.37005 4.28755 1.30985C4.31361 1.24965 4.35173 1.19543 4.39957 1.15054C4.44446 1.10271 4.49867 1.06459 4.55887 1.03853C4.61907 1.01247 4.68397 0.999023 4.74957 0.999023C4.81516 0.999023 4.88006 1.01247 4.94026 1.03853C5.00046 1.06459 5.05468 1.10271 5.09957 1.15054L11.5996 7.65054C11.6474 7.69543 11.6855 7.74965 11.7116 7.80985C11.7376 7.87005 11.7511 7.93495 11.7511 8.00054C11.7511 8.06614 11.7376 8.13104 11.7116 8.19124C11.6855 8.25143 11.6474 8.30565 11.5996 8.35054L5.09957 14.8505C5.05492 14.8986 5.00072 14.9367 4.94044 14.9626C4.88016 14.9884 4.81514 15.0013 4.74957 15.0005Z" fill="grey"></path>
                  </svg>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className={`ge-product-family-carousel-slider-container ${currentSlideIndex === 0 ? 'align-with-title' : 'align-left'}`}>
        <div className="ge-product-family-carousel-slider-wrapper">
          <GeSlider settings={slickSettings} className="ge-product-family-carousel-slider">
            {tabSlider}
          </GeSlider>
        </div>
      </div>
    </div>
  );
}

export default ProductFamily;

